<script lang="ts">
  import {
    checkGatewayHealth,
    handleMicrosoftAuthError,
    isMicrosoftAuthAvailable,
    loadAuthConfig,
    getMe,
    loginWithMicrosoft,
    loginWithPassword,
    type ChattyAuthConfig,
    type ChattyAuthStatus,
    type ChattySession,
  } from "../auth/chatty-auth";
  import {
    AuthDialogCancelled,
    acquireMicrosoftIdTokenViaOfficeDialog,
    isAuthDialogWindow,
    runAuthDialogFlow,
  } from "../auth/chatty-auth-dialog";
  import ChattyLoginPage from "./chatty-login-page.svelte";

  interface Props {
    appName?: string;
    pluginName?: string;
    dashboardUrl?: string;
  }

  let { appName = "Chatty", dashboardUrl }: Props = $props();

  let status = $state<ChattyAuthStatus>("checking");
  let session = $state<ChattySession | null>(null);
  let error = $state<string | null>(null);
  let submitting = $state(false);
  let authConfig = $state<ChattyAuthConfig | null>(null);
  let authConfigLoading = $state(false);

  function getDashboardUrl(): string {
    const configured = dashboardUrl ?? (import.meta.env.VITE_CHATTY_DASHBOARD_URL as string | undefined);
    if (configured) return configured.replace(/\/$/, "");
    if (window.location.hostname === "localhost") return "http://localhost:3012";
    return "https://dashboard.chatty-ai.ai";
  }

  async function refreshAuthConfig() {
    authConfigLoading = true;
    try {
      authConfig = await loadAuthConfig();
    } catch {
      authConfig = null;
      error = "auth_config_unavailable";
    } finally {
      authConfigLoading = false;
    }
  }

  async function initialize() {
    status = "checking";
    error = null;

    // If THIS window is the Office auth dialog, don't boot the app: just complete the Microsoft
    // redirect and post the id_token back to the taskpane. Stays in "checking" so no UI renders.
    if (isAuthDialogWindow()) {
      await refreshAuthConfig();
      await runAuthDialogFlow(authConfig?.azure_client_id);
      return;
    }

    const gatewayOk = await checkGatewayHealth();
    if (!gatewayOk) {
      status = "gateway_unavailable";
      error = "gateway_unavailable";
      return;
    }

    await refreshAuthConfig();

    const current = await getMe();
    if (current) {
      session = current;
      status = "logged_in";
      return;
    }

    status = "logged_out";
  }

  async function handleEmailLogin(email: string, password: string) {
    submitting = true;
    error = null;
    try {
      const loggedIn = await loginWithPassword(email, password);
      session = loggedIn;
      status = "logged_in";
    } catch (err) {
      error = err instanceof Error ? err.message : "login_failed";
      status = "logged_out";
    } finally {
      submitting = false;
    }
  }

  async function handleMicrosoftLogin() {
    if (!authConfig?.azure_client_id) {
      error = "microsoft_sso_not_configured";
      return;
    }

    submitting = true;
    error = null;
    try {
      // PREFERRED: Office-native dialog. Avoids the MSAL popup entirely, and with it the
      // "Cross-Origin-Opener-Policy policy would block the window.closed call" failure that
      // breaks loginPopup inside the Office taskpane WebView.
      // Returns null whenever the dialog route isn't available or didn't produce a token, in
      // which case we fall through to the original popup flow (worst case = previous behaviour).
      let idToken: string | null = null;
      try {
        idToken = await acquireMicrosoftIdTokenViaOfficeDialog();
      } catch (dialogErr) {
        if (dialogErr instanceof AuthDialogCancelled) {
          // The user deliberately closed the dialog — don't ambush them with a second popup.
          error = null;
          status = "logged_out";
          return;
        }
        idToken = null;
      }

      // FALLBACK: the original MSAL popup flow, unchanged.
      if (!idToken) {
        const { PublicClientApplication } = await import("@azure/msal-browser");
        const msalInstance = new PublicClientApplication({
          auth: {
            clientId: authConfig.azure_client_id,
            authority: "https://login.microsoftonline.com/common",
            redirectUri: window.location.origin,
          },
          cache: { cacheLocation: "localStorage" },
        });
        await msalInstance.initialize();
        const result = await msalInstance.loginPopup({
          scopes: ["openid", "profile", "email"],
          prompt: "select_account",
        });
        idToken = result.idToken ?? null;
      }
      if (!idToken) throw new Error("invalid_microsoft_token");

      const loginResult = await loginWithMicrosoft(idToken);
      if (loginResult.action === "logged_in") {
        session = { user: loginResult.user, org: loginResult.org };
        status = "logged_in";
        return;
      }

      error = loginResult.action;
      status = "logged_out";
    } catch (err) {
      error = handleMicrosoftAuthError(err);
      status = "logged_out";
    } finally {
      submitting = false;
    }
  }

  $effect(() => {
    void initialize();
  });
</script>

{#if status !== "checking" && status !== "logged_in"}
  <ChattyLoginPage
    {error}
    {submitting}
    authConfigLoading={authConfigLoading}
    microsoftEnabled={isMicrosoftAuthAvailable(authConfig)}
    appName={appName}
    dashboardUrl={getDashboardUrl()}
    onEmailLogin={handleEmailLogin}
    onMicrosoftLogin={handleMicrosoftLogin}
    onRetry={initialize}
  />
{/if}
